import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { Activity, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useObservabilityCommandCenter, useRunPerformancePass } from './observability.hooks';

export function ObservabilityCommandCenterPage() {
  const { permissions } = useAuth();
  const { formatDateTime: formatDate } = useUserPreferences();
  const dashboardQuery = useObservabilityCommandCenter();
  const passMutation = useRunPerformancePass();
  const canAdmin = hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN);
  const runError = passMutation.error instanceof Error ? passMutation.error.message : null;

  if (!canAdmin) {
    return (
      <EmptyState
        icon={<Activity className="h-5 w-5" aria-hidden="true" />}
        title="Observability access required"
        description={`Your role needs ${PERMISSIONS.PLATFORM_ADMIN} to view performance observability.`}
      />
    );
  }

  if (dashboardQuery.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ErrorState
        title="Unable to load observability"
        description={
          dashboardQuery.error instanceof Error
            ? dashboardQuery.error.message
            : 'Unknown observability error'
        }
        action={
          <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const data = dashboardQuery.data;

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow="S36 · Performance Observability"
          title="Performance Observability"
          description="Live cache, metrics, traces, request-id logs, and audited performance passes against the running API process."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Refresh
              </Button>
              <Button
                size="sm"
                disabled={passMutation.isPending || !data.permissions.canRunPerformancePass}
                onClick={() => {
                  passMutation.reset();
                  passMutation.mutate();
                }}
              >
                <Activity className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {passMutation.isPending ? 'Running…' : 'Run performance pass'}
              </Button>
            </div>
          }
        />
      }
      toolbar={
        <>
          {runError ? <p className="text-destructive text-sm">{runError}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">HTTP requests</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.live.httpRequestsTotal}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">p95 latency</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.live.httpDurationMsCount === 0 ? '—' : `${data.live.httpDurationMsP95}ms`}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Cache hit rate
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{data.live.cache.hitRate}%</p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">HTTP errors</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.live.httpErrorsTotal}
              </p>
            </Card>
          </div>
        </>
      }
      sections={[
        {
          id: 'observability-controls',
          label: 'Observability controls',
          content: (
            <Card className="overflow-hidden">
              <div className="hx-section-header border-b px-3 py-2">
                <h2 className="hx-section-title text-sm font-semibold">Observability controls</h2>
              </div>
              <ul className="divide-y">
                {data.controls.map((control) => (
                  <li
                    key={control.id}
                    className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">{control.name}</p>
                      <p className="text-muted-foreground text-sm">{control.description}</p>
                    </div>
                    <Badge variant="outline">{control.category}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ),
        },
        {
          id: 'recent-traces',
          label: 'Recent traces',
          content: (
            <Card className="overflow-hidden">
              <div className="hx-section-header border-b px-3 py-2">
                <h2 className="hx-section-title text-sm font-semibold">Recent traces</h2>
              </div>
              {data.live.recentTraces.length === 0 ? (
                <EmptyState
                  className="border-0"
                  title="No traces yet"
                  description="HTTP spans appear as traffic hits the API interceptor."
                />
              ) : (
                <ul className="divide-y">
                  {data.live.recentTraces.slice(0, 12).map((trace) => (
                    <li key={`${trace.requestId}-${trace.startedAt}`} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium">
                          {trace.method} {trace.path}
                        </p>
                        <Badge variant={trace.error ? 'critical' : 'outline'}>
                          {trace.statusCode}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {trace.durationMs}ms · {trace.requestId}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
        {
          id: 'performance-pass-history',
          label: 'Performance pass history',
          content: (
            <Card className="overflow-hidden">
              <div className="hx-section-header border-b px-3 py-2">
                <h2 className="hx-section-title text-sm font-semibold">Performance pass history</h2>
              </div>
              {data.passes.length === 0 ? (
                <EmptyState
                  className="border-0"
                  title="No performance passes"
                  description="Run a performance pass to persist audited cache/metrics/trace/log findings."
                />
              ) : (
                <ul className="divide-y">
                  {data.passes.map((pass) => (
                    <li key={pass.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium">{pass.passNumber}</p>
                        <Badge variant="outline">{pass.status}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm">{pass.summary}</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {pass.findingsPassed}/{pass.controlsChecked} passed ·{' '}
                        {formatDate(pass.completedAt ?? pass.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
      ]}
    />
  );
}
